import React, { useEffect, useState } from 'react';
import { X, Crown, Mail, Calendar, Clock, UserCheck, UserX, Globe, MessageSquare, Edit } from 'lucide-react';
import { TeamMember } from '../../store/teamStore';
import { teamService } from '../../services/team.service';

interface ViewMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  onEdit: () => void;
}

const ViewMemberModal: React.FC<ViewMemberModalProps> = ({ isOpen, onClose, member, onEdit }) => {
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && member) {
      loadPermissions();
    }
  }, [isOpen, member]);

  const loadPermissions = async () => {
    if (!member) return;
    try {
      const allPermissions = await teamService.getRolePermissions();
      setRolePermissions(allPermissions[member.role] || []);
    } catch (error) {
      console.error('Failed to load permissions', error);
    }
  };

  if (!isOpen || !member) return null;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Admin': return <Crown className="w-4 h-4" />;
      case 'Editor': return <Edit className="w-4 h-4" />;
      case 'Viewer': return <UserCheck className="w-4 h-4" />;
      case 'Analyst': return <Globe className="w-4 h-4" />;
      default: return <UserCheck className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      'Admin': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Editor': 'bg-primary-100 text-primary-800 border-primary-200',
      'Viewer': 'bg-green-100 text-green-800 border-green-200',
      'Analyst': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Online': return <div className="w-2 h-2 bg-green-500 rounded-full"></div>;
      case 'Offline': return <div className="w-2 h-2 bg-gray-400 rounded-full"></div>;
      case 'Away': return <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>;
      default: return <div className="w-2 h-2 bg-gray-400 rounded-full"></div>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatLastActive = (dateString: string | undefined) => {
    if (!dateString) return 'Never';

    const now = new Date();
    const lastActive = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return formatDate(dateString);
  };

  // Format permission string to readable label
  const formatPermissionLabel = (permission: string) => {
    return permission
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Team Member Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Header Section */}
            <div className="flex items-start gap-4">
              {member.avatar ? (
                <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center text-white font-semibold text-2xl">
                  {member.name.charAt(0)}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                  {member.isOwner && <Crown className="w-4 h-4 text-yellow-500" />}
                </div>
                <p className="text-gray-600">{member.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(member.role)}`}>
                    {getRoleIcon(member.role)}
                    <span className="ml-1">{member.role}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                    {getStatusIcon(member.status)}
                    <span>{member.status}</span>
                  </span>
                </div>
              </div>
              <button
                onClick={onEdit}
                className="flex items-center gap-1 px-3 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Member Since</div>
                <div className="flex items-center gap-1 mt-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{formatDate(member.joinedAt)}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Last Active</div>
                <div className="flex items-center gap-1 mt-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{formatLastActive(member.lastActive)}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Status</div>
                <div className="flex items-center gap-1 mt-1">
                  {member.status === 'Online' ? (
                    <UserCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <UserX className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900">{member.status}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">Role Level</div>
                <div className="mt-1">
                  <span className="font-medium text-gray-900">
                    {member.role === 'Admin' ? 'Full Access' :
                      member.role === 'Editor' ? 'Editor Access' :
                        member.role === 'Analyst' ? 'Analyst Access' : 'View Only'}
                  </span>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Permissions</h4>
              {rolePermissions.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {rolePermissions.map((permission) => (
                    <div key={permission} className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      <span className="text-sm">{formatPermissionLabel(permission)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm italic">No specific permissions assigned.</div>
              )}
            </div>

            {/* Contact Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors">
                <Mail className="w-4 h-4" />
                <span>Send Email</span>
              </button>

              <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors">
                <MessageSquare className="w-4 h-4" />
                <span>Send Message</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMemberModal;